import * as React from 'react';
import { useParams } from 'react-router-dom';

import { Rule } from '@travelnest/merlin';

import { http } from '../../../../../api';
import { BackButton } from '../../../../components/back-button/BackButton';
import { Loader } from '../../../../components/loader/Loader';
import { Content } from '../../../../components/page';
import { useDataLoad } from '../../../../helpers/hooks';

interface ICustomer {
	id: string;
	name: string;
	email: string;
	phone: string;
	active: boolean;
	address: string;
	website: string;
	check_in: string | null;
	language: string;
	max_stay: string | null;
	min_stay: string | null;
	postcode: string;
	check_out: string | null;
	validated: boolean;
	created_at: string;
	updated_at: string;
	company_name: string | null;
	emergency_phone: string;
	notice_required: string | null;
	advance_bookings: string | null;
	security_deposit: string | null;
	balance_paid_in_advance: string | null;
	number_of_reported_properties: string | null;
}

interface IAuditLog {
	id: string;
	table_name: string;
	version: number;
	operation: string;
	diff: string;
	actor: string;
	trace: string;
	timestamp: string;
}

type ParsedAuditLog<T> = {
	[K in keyof Omit<IAuditLog, 'diff'>]: IAuditLog[K];
} & {
	diff: T;
};

type Entries<T, Keys extends keyof T = keyof T> = Array<{
	[K in Keys]: [K, T[K]];
}[Keys]>;

type LogTypes = 'customer' | 'account' | 'financial' | 'churn';

const ClassNames = {
	TypeButton: 'admin-customer-history__type-button',
	VersionButton: 'admin-customer-history__version-button'
};

const VersionDiff: React.FC<{ current: ParsedAuditLog<ICustomer>, prev?: ParsedAuditLog<ICustomer> }> = ({ current, prev }) => {
	const base = prev?.diff ?? current.diff ?? {};
	const next = current.diff;
	const entries = Object.entries(base) as Entries<ICustomer>;
	return <div className="admin-customer-history__diff">
		{entries.sort().map(([key, value]) => {
			const nextval = next[key as keyof ICustomer];
			if (!(key in next) || nextval === value) return <div className="admin-customer-history__diff-row" key={key}>{key}: {String(value)}</div>;
			return <div className="admin-customer-history__diff-row admin-customer-history__diff-row--highlight" key={key}>
				{key}: <><del>{String(value)}</del> &raquo; <span>{String(nextval)}</span></>
			</div>;
		})}
	</div>;
};

const VersionViewer: React.FC<{ current: ParsedAuditLog<ICustomer>, prev?: ParsedAuditLog<ICustomer> }> = ({ current, prev }) => {
	const log = Object.assign({}, prev || {}, current);
	const entries = Object.entries(log) as Entries<IAuditLog>;
	return <dl className="admin-customer-history__version-list">
		{entries.sort().map(([key, value]) => <React.Fragment key={key}>
			<dt className="admin-customer-history__version-key">{key}</dt>
			<dd className="admin-customer-history__version-value">
				{key === 'diff' ? <VersionDiff current={current} prev={prev} /> : value}
			</dd>
		</React.Fragment>)}
	</dl>;
};

export const AdminCustomerHistory: React.FC = () => {
	const { id } = useParams();

	const [logs, setLogs] = React.useState<Array<ParsedAuditLog<ICustomer>>>([]);
	const [meta, setMeta] = React.useState({ customer: id || '', financial: '', churn: '' });
	const [shouldLoad, setShouldLoad] = React.useState<Record<string, boolean>>({ meta: true, logs: true });
	const [activeVersion, setActiveVersion] = React.useState<number>(1);
	const [activeLogType, setActiveLogType] = React.useState<LogTypes>('customer');
	const [activeEntityId, setActiveEntityId] = React.useState<string>(id || '');

	const setLogType = (type: LogTypes) => {
		switch (type) {
			case 'customer':
				setActiveEntityId(meta.customer);
				break;
			case 'financial':
				setActiveEntityId(meta.financial);
				break;
			case 'churn':
				setActiveEntityId(meta.churn);
				break;
			default:
				break;
		}
		setLogs([]);
		setActiveLogType(type);
		setShouldLoad({ meta: shouldLoad.meta, logs: true });
	};

	const loadingMeta = useDataLoad(shouldLoad.meta, async () => {
		const customer = await http.get(`/customers/${id}`);
		const financial = await http.get(`/customers/${id}/financial`);
		setMeta({
			customer: customer.id,
			financial: financial.id,
			churn: customer.churn?.id,
		});
		setShouldLoad({ logs: shouldLoad.logs, meta: false });
	});

	const loading = useDataLoad(shouldLoad.logs, async () => {
		const data: IAuditLog[] = await http.get(`/customers/audit/${activeLogType}/${activeEntityId}`);
		setShouldLoad({ logs: false, meta: shouldLoad.meta });
		setLogs(data.sort((a, b) => a.version - b.version).map((d) => ({ ...d, diff: JSON.parse(d.diff as string) })));
		setActiveVersion(data.length);
	});

	if (loading || loadingMeta) return <Loader loading={loading} />;

	const log = logs.find((l) => l.version === activeVersion) || logs[0];
	const base = logs.filter((l) => l.version < activeVersion)
		.reduce((acc, l) => {
			const { diff, ...rest } = l;
			return Object.assign(acc, {
				...rest,
				diff: Object.assign({}, acc.diff, diff)
			});
		}, {} as ParsedAuditLog<ICustomer>);

	return <Content type="full">
		<div style={{ display: 'flex', alignItems: 'center' }}>
			<BackButton to={`/admin/customer/${id}/edit`} />
			<h2>Auditing: {activeLogType} data changelog for owner {id}</h2>
		</div>
		<Rule />
		<h3>Version {activeVersion}</h3>
		<nav className="admin-customer-history__type-nav">
			<button
				className={`${ClassNames.TypeButton} ${activeLogType === 'customer' ? `${ClassNames.TypeButton}--active` : ''}`}
				disabled={!meta.customer}
				onClick={() => setLogType('customer')}>Customer</button>
			<button
				className={`${ClassNames.TypeButton} ${activeLogType === 'financial' ? `${ClassNames.TypeButton}--active` : ''}`}
				disabled={!meta.financial}
				onClick={() => setLogType('financial')}>Financial</button>
			<button
				className={`${ClassNames.TypeButton} ${activeLogType === 'churn' ? `${ClassNames.TypeButton}--active` : ''}`}
				disabled={!meta.churn}
				onClick={() => setLogType('churn')}>Churn</button>
		</nav>
		<div className="admin-customer-history__container">
			<nav className="admin-customer-history__version-nav">
				{[...logs].sort((a, b) => b.version - a.version).map((l) => <button
					className={`${ClassNames.VersionButton} ${l.version === activeVersion ? `${ClassNames.VersionButton}--active` : ''}`}
					onClick={() => setActiveVersion(l.version)}
					key={l.version}
					value={l.version}>
					Version {l.version} ({l.timestamp})
				</button>)}
			</nav>

			<VersionViewer current={log} prev={base} />
		</div>
	</Content>;
};
